/**
 * RC-2 FIX: Package Configuration — Pure Helpers Only
 * 
 * ALL package data comes from PackageCatalogContext (which fetches from backend).
 * This file provides ONLY:
 * - FEATURE_DEFINITIONS: UI display labels for feature keys (presentation layer)
 * - formatPrice: Pure formatting helper
 * - Snapshot-based feature/limit check helpers
 * - Trial status calculation helpers
 * 
 * REMOVED: PACKAGE_CATALOG, PACKAGE_TIERS, all static data, all mocked admin ops.
 */

// =============================================================================
// FEATURE DEFINITIONS (UI display labels only — NOT a data source)
// =============================================================================

export const FEATURE_DEFINITIONS = {
  campaigns_enabled: {
    name: 'Campaigns',
    description: 'Run marketing campaigns',
    category: 'core',
  },
  leads_enabled: {
    name: 'Leads Management',
    description: 'Track and manage patient leads',
    category: 'core',
  },
  priority_support: {
    name: 'Priority Support',
    description: 'Priority support service level',
    category: 'service_metadata',
  },
  dedicated_support: {
    name: 'Dedicated Support',
    description: 'Dedicated support service',
    category: 'service_metadata',
  },
};

// PACKAGE-ENTITLEMENT-SSOT-REMEDIATION (2026-06)
// Technically-enforced software capabilities (backend-authoritative).
export const ENFORCED_FEATURE_KEYS = ['leads_enabled', 'campaigns_enabled'];
// Commercial SERVICE metadata — marketed & parametric, but NOT software-enforced.
export const SERVICE_METADATA_KEYS = ['priority_support', 'dedicated_support'];

// =============================================================================
// FORMATTING HELPERS
// =============================================================================

/**
 * Format price for display
 * @param {Object} pricing - Package pricing object { amount, currency, interval, customPricing }
 * @returns {string} Formatted price string
 */
export const formatPrice = (pricing) => {
  if (!pricing || pricing.customPricing) {
    return 'Contact Sales';
  }
  if (pricing.amount === 0 || pricing.amount === null) {
    return 'Free';
  }
  
  const symbol = pricing.currency === 'EUR' ? '\u20AC' : pricing.currency === 'USD' ? '$' : pricing.currency === 'GBP' ? '\u00A3' : '\u20AC';
  const interval = pricing.interval === 'month' || pricing.interval === 'monthly' ? '/mo'
    : pricing.interval === 'year' || pricing.interval === 'yearly' ? '/yr'
    : pricing.interval === 'quarterly' ? '/qtr'
    : '';
  
  return `${symbol}${pricing.amount}${interval}`;
};

// =============================================================================
// SNAPSHOT-BASED FEATURE ACCESS
// =============================================================================

/**
 * Create a subscription snapshot from a package definition
 * @param {Object} packageDef - Package definition from catalog
 * @returns {Object} Immutable package snapshot
 */
export const createPackageSnapshot = (packageDef) => {
  return {
    id: packageDef.id,
    version: packageDef.version,
    name: packageDef.name,
    displayName: packageDef.displayName,
    description: packageDef.description,
    color: packageDef.color,
    badgeColor: packageDef.badgeColor,
    icon: packageDef.icon,
    pricing: { ...packageDef.pricing },
    duration: packageDef.duration ? { ...packageDef.duration } : null,
    limits: { ...packageDef.limits },
    features: { ...packageDef.features },
    upgradePrompt: packageDef.upgradePrompt,
    snapshotCreatedAt: new Date().toISOString(),
    sourcePackageVersion: packageDef.version,
  };
};

/**
 * Check if a feature is available in a package snapshot
 * @param {Object} packageSnapshot - Subscription's package snapshot
 * @param {string} featureName - Feature key
 * @returns {boolean|string} Feature availability
 */
export const hasFeatureInSnapshot = (packageSnapshot, featureName) => {
  if (!packageSnapshot?.features) return false;
  return packageSnapshot.features[featureName] ?? false;
};

/**
 * Check if a limit is reached in a package snapshot
 * @param {Object} packageSnapshot - Subscription's package snapshot
 * @param {string} limitName - Limit key
 * @param {number} currentCount - Current usage
 * @returns {Object} Limit check result
 */
export const checkLimitInSnapshot = (packageSnapshot, limitName, currentCount) => {
  if (!packageSnapshot?.limits) {
    return { allowed: false, limit: 0, remaining: 0, unlimited: false };
  }
  
  const limit = packageSnapshot.limits[limitName];
  
  if (limit === -1) {
    return { allowed: true, limit: -1, remaining: -1, unlimited: true };
  }
  
  const remaining = Math.max(0, limit - currentCount);
  return {
    allowed: currentCount < limit,
    limit,
    remaining,
    unlimited: false,
  };
};

// =============================================================================
// TRIAL-SPECIFIC HELPERS
// =============================================================================

/**
 * Calculate trial status from trial info
 * @param {Object} trialInfo - Trial information { startDate, extended }
 * @param {Object} packageSnapshot - Package snapshot for duration config
 * @returns {Object} Trial status
 */
export const calculateTrialStatus = (trialInfo, packageSnapshot = null) => {
  if (!trialInfo?.startDate) {
    return { active: false, expired: false, daysRemaining: 0, totalDays: 0, currentDay: 0 };
  }
  
  const startDate = new Date(trialInfo.startDate);
  const now = new Date();
  
  const duration = packageSnapshot?.duration || { defaultDays: 14, maxExtendedDays: 21 };
  const maxDays = trialInfo.extended 
    ? (duration.maxExtendedDays || 21)
    : (duration.defaultDays || 14);
  
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + maxDays);
  
  const msRemaining = endDate.getTime() - now.getTime();
  const daysRemaining = Math.max(0, Math.ceil(msRemaining / (1000 * 60 * 60 * 24)));
  
  const msElapsed = now.getTime() - startDate.getTime();
  const daysElapsed = Math.floor(msElapsed / (1000 * 60 * 60 * 24));
  const currentDay = Math.min(Math.max(1, daysElapsed + 1), maxDays + 1);
  
  const isLastDay = daysRemaining === 1;
  const isAlmostExpired = daysRemaining <= 1;
  const canExtend = !trialInfo.extended && (duration.maxExtendedDays > duration.defaultDays);
  
  return {
    active: daysRemaining > 0,
    expired: daysRemaining <= 0,
    daysRemaining,
    totalDays: maxDays,
    currentDay,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    extended: trialInfo.extended || false,
    isLastDay,
    isAlmostExpired,
    canExtend,
    defaultDays: duration.defaultDays || 14,
    maxExtendedDays: duration.maxExtendedDays || 21,
  };
};

/**
 * Format trial badge text
 * @param {Object} trialStatus - Trial status object
 * @returns {string} Badge text
 */
export const formatTrialBadge = (trialStatus) => {
  if (!trialStatus) return '';
  
  if (trialStatus.expired) {
    return 'Trial Expired';
  }
  
  const dayNum = trialStatus.totalDays - trialStatus.daysRemaining + 1;
  return `Trial: Day ${dayNum} of ${trialStatus.totalDays}`;
};
